import React, { useState, useEffect, useCallback, useMemo } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import "./SpreadsheetViewer.css";

const SpreadsheetViewer = ({
  data = [],
  onCellSelected = () => {},
  isLinking = false,
  onSheetChange = () => {},
  fileName,
  onClose,
}) => {
  // Validate data structure
  const validSheets = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.filter((sheet) => {
      const isValid =
        sheet &&
        sheet.name &&
        Array.isArray(sheet.data) &&
        sheet.data.length > 0;

      if (!isValid) {
      }
      return isValid;
    });
  }, [data]);

  const [activeSheet, setActiveSheet] = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);

  if (validSheets.length === 0) {
    return <div>No valid sheet data available</div>;
  }

  useEffect(() => {
    // Removed excessive logging for data structure
  }, [data]);

  const handleCellClick = (sheetName, rowIndex, cellIndex, value) => {
    setSelectedCell({
      sheetName,
      rowIndex,
      cellIndex,
      value,
    });
    onCellSelected({
      sheetName,
      rowIndex,
      cellIndex,
      value,
    });
  };

  const handleSheetChange = useCallback(
    (index) => {
      if (index !== activeSheet) {
        setActiveSheet(index);
        onSheetChange(index);
      }
    },
    [activeSheet, onSheetChange]
  );

  // Initialize activeSheet when data changes, but don't call onSheetChange
  useEffect(() => {
    if (validSheets.length > 0 && activeSheet >= validSheets.length) {
      setActiveSheet(0);
    }
  }, [validSheets.length, activeSheet]);

  return (
    <div
      className={`spreadsheet-viewer ${isLinking ? "cursor-crosshair" : ""}`}
    >
      <div className="sheet-tabs">
        {validSheets.map((sheet, index) => (
          <button
            key={sheet.name}
            className={index === activeSheet ? "active" : ""}
            onClick={() => handleSheetChange(index)}
          >
            {sheet.name}
          </button>
        ))}
        {(fileName || onClose) && (
          <div className="sheet-tabs-end">
            {fileName && (
              <span className="sheet-file-name" title={fileName}>
                {fileName}
              </span>
            )}
            {onClose && (
              <button
                type="button"
                className="sheet-close"
                aria-label="Close preview"
                onClick={onClose}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="sheet-container">
        {validSheets.map((sheet, sheetIndex) => (
          <div
            key={sheet.name}
            className={`sheet ${sheetIndex === activeSheet ? "active" : ""}`}
          >
            <div className="table-container">
              <table>
                <thead>
                  {sheet.data && sheet.data.length > 0 && (
                    <tr>
                      {sheet.data[0].map((header, index) => (
                        <th
                          key={index}
                          onClick={() =>
                            handleCellClick(sheet.name, 0, index, header)
                          }
                          className={
                            selectedCell &&
                            selectedCell.sheetName === sheet.name &&
                            selectedCell.rowIndex === 0 &&
                            selectedCell.cellIndex === index
                              ? "selected"
                              : ""
                          }
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {sheet.data &&
                    sheet.data.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className={
                              selectedCell &&
                              selectedCell.sheetName === sheet.name &&
                              selectedCell.rowIndex === rowIndex + 1 &&
                              selectedCell.cellIndex === cellIndex
                                ? "selected"
                                : ""
                            }
                            onClick={() =>
                              handleCellClick(
                                sheet.name,
                                rowIndex + 1,
                                cellIndex,
                                cell
                              )
                            }
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpreadsheetViewer;
